/**
 * Demo analytics data generator.
 *
 * Business model for a padel club with two time-window groups:
 *   - HC (Heures Creuses / off-peak): 18 slots/day × 30 = 540 slots/month @ 36 €
 *   - HP (Heures Pleines / peak hours): 9 slots/day × 30 = 270 slots/month @ 52 €
 *   - 1 réservation = 4 utilisateurs (players)
 *
 * Monthly occupancy (linear growth months 4→6):
 *   M1: HC 38.00% / HP 76.00%  →  410.4 bookings,  CA 18 057.60 €,  1 642 users
 *   M2: HC 40.00% / HP 80.00%  →  432.0 bookings,  CA 19 008.00 €,  1 728 users
 *   M3: HC 39.00% / HP 78.00%  →  421.2 bookings,  CA 18 532.80 €,  1 685 users
 *   M4: HC 49.67% / HP 84.67%  →  496.8 bookings,  CA 21 542.40 €,  1 987 users
 *   M5: HC 60.33% / HP 91.33%  →  572.4 bookings,  CA 24 552.00 €,  2 290 users
 *   M6: HC 71.00% / HP 98.00%  →  648.0 bookings,  CA 27 561.60 €,  2 592 users
 *
 * TRANSITION_DATE is set to Aug 21 2025 so that exactly 6 × 30 = 180 days
 * fit before DATA_END (Feb 16 2026). This makes the dashboard's "Last 30 Days"
 * align with Month 6, "Last 90 Days" with Months 4+5+6, etc., producing the
 * exact comparison percentages from the business plan:
 *   30d: +12.26% CA, +13.21% users
 *   90d: +32.49% CA, +35.90% users
 */

// ---------------------------------------------------------------------------
// Demo organization IDs and dataset mapping
// ---------------------------------------------------------------------------

/** All organization IDs that show demo/placeholder analytics. */
export const DEMO_ORGANIZATION_IDS: readonly string[] = [
    '78570199-7d41-4e98-86fb-1d2cbd919f7e', // Club 1 – scenario 1 (Données de base)
    'd0c9e4e4-30cc-487f-86c2-4a1abdaceb85', // Club 4 courts – scénario final
    'fed17648-9acc-46c7-97ed-3861aef98449', // Club 1 – scenario 2 (Mysportset fin M3)
];

/** First demo org ID (backward compatibility). */
export const DEMO_ORGANIZATION_ID = DEMO_ORGANIZATION_IDS[0];

/**
 * Maps a demo organization ID to a dataset key. Add entries when you have
 * org-specific data (e.g. a second club with different constants).
 * Omitted orgs use the 'default' dataset.
 */
export const DEMO_ORG_TO_DATASET: Partial<Record<string, string>> = {
    [DEMO_ORGANIZATION_IDS[0]]: 'default',
    [DEMO_ORGANIZATION_IDS[1]]: 'club1-scenario2',
    [DEMO_ORGANIZATION_IDS[2]]: 'club4-courts',
};

const DEFAULT_DATASET_KEY = 'default';

function getDatasetKey(orgId?: string): string {
    if (!orgId || !DEMO_ORGANIZATION_IDS.includes(orgId)) return DEFAULT_DATASET_KEY;
    return DEMO_ORG_TO_DATASET[orgId] ?? DEFAULT_DATASET_KEY;
}

// ---------------------------------------------------------------------------
// Dataset config (per-org or per-scenario constants)
// ---------------------------------------------------------------------------

export interface DemoDatasetConfig {
    dataStart: Date;
    dataEnd: Date;
    transitionDate: Date;
    daysPerMonth: number;
    hcSlotsPerMonth: number;
    hpSlotsPerMonth: number;
    hcSlotsPerDay: number;
    hpSlotsPerDay: number;
    priceHc: number;
    priceHp: number;
    usersPerBooking: number;
    monthlyOccupancy: { hcRate: number; hpRate: number }[];
    monthlyTargets: { hcBookings: number; hpBookings: number; totalBookings: number; totalRevenue: number; users: number }[];
    monthlyUserBase: number[];
    monthlyBookingsBase: number[];
    monthlyRevenueBase: number[];
    monthlyHcPct: number[];
    monthlyHpPct: number[];
    monthlyEvents: number[];
    monthlySubscriptions: number[];
}

/** Club 1 – 7 courts: HC 9h/jour, HP 5h/jour → 1 890 + 1 050 créneaux/mois */
function buildDefaultDatasetConfig(): DemoDatasetConfig {
    const DAYS_PER_MONTH = 30;
    const COURTS = 7;
    const HC_HOURS_PER_DAY = 9;
    const HP_HOURS_PER_DAY = 5;
    const HC_SLOTS_PER_MONTH = HC_HOURS_PER_DAY * DAYS_PER_MONTH * COURTS; // 1 890
    const HP_SLOTS_PER_MONTH = HP_HOURS_PER_DAY * DAYS_PER_MONTH * COURTS; // 1 050
    const HC_SLOTS_PER_DAY = HC_HOURS_PER_DAY * COURTS;
    const HP_SLOTS_PER_DAY = HP_HOURS_PER_DAY * COURTS;
    const PRICE_HC = 36;
    const PRICE_HP = 52;
    const USERS_PER_BOOKING = 2.5;

    const monthlyOccupancy = [
        { hcRate: 0.51, hpRate: 0.82 },
        { hcRate: 0.53, hpRate: 0.85 },
        { hcRate: 0.49, hpRate: 0.79 },
        { hcRate: 0.6367, hpRate: 0.8667 },
        { hcRate: 0.7133, hpRate: 0.9533 },
        { hcRate: 0.80, hpRate: 0.99 },
    ];

    const monthlyTargets = [
        { hcBookings: 963.9, hpBookings: 861, totalBookings: 1824.9, totalRevenue: 79472.40, users: 4562 },
        { hcBookings: 1001.7, hpBookings: 892.5, totalBookings: 1894.2, totalRevenue: 82471.20, users: 4635 },
        { hcBookings: 926.1, hpBookings: 829.5, totalBookings: 1755.6, totalRevenue: 76473.60, users: 4790 },
        { hcBookings: 1203.36, hpBookings: 910.04, totalBookings: 2113.4, totalRevenue: 90643.04, users: 5883 },
        { hcBookings: 1348.14, hpBookings: 1000.97, totalBookings: 2349.11, totalRevenue: 100583.48, users: 6673 },
        { hcBookings: 1512, hpBookings: 1039.5, totalBookings: 2551.5, totalRevenue: 108486, users: 7080 },
    ];

    const dataStart = new Date('2025-08-21');
    const dataEnd = new Date('2026-02-16');
    const transitionDate = new Date('2025-08-21');

    return {
        dataStart,
        dataEnd,
        transitionDate,
        daysPerMonth: DAYS_PER_MONTH,
        hcSlotsPerMonth: HC_SLOTS_PER_MONTH,
        hpSlotsPerMonth: HP_SLOTS_PER_MONTH,
        hcSlotsPerDay: HC_SLOTS_PER_DAY,
        hpSlotsPerDay: HP_SLOTS_PER_DAY,
        priceHc: PRICE_HC,
        priceHp: PRICE_HP,
        usersPerBooking: USERS_PER_BOOKING,
        monthlyOccupancy,
        monthlyTargets,
        monthlyUserBase: [4562, 4635, 4790, 5883, 6673, 7080],
        monthlyBookingsBase: [1825, 1894, 1756, 2113, 2349, 2552],
        monthlyRevenueBase: [79472.40, 82471.20, 76473.60, 90643.04, 100583.48, 108486],
        monthlyHcPct: [51, 53, 49, 63.67, 71.33, 80],
        monthlyHpPct: [82, 85, 79, 86.67, 95.33, 99],
        monthlyEvents: [5, 6, 5, 8, 10, 12],
        monthlySubscriptions: [20, 22, 18, 28, 32, 36],
    };
}

/** Club 1 – scenario 2: same 7 courts, Mysportset installé fin M3, CA M4–M6 ≈ 88k / 98k / 106k */
function buildClub1Scenario2Config(): DemoDatasetConfig {
    const DAYS_PER_MONTH = 30;
    const COURTS = 7;
    const HC_HOURS_PER_DAY = 9;
    const HP_HOURS_PER_DAY = 5;
    const HC_SLOTS_PER_MONTH = HC_HOURS_PER_DAY * DAYS_PER_MONTH * COURTS; // 1 890
    const HP_SLOTS_PER_MONTH = HP_HOURS_PER_DAY * DAYS_PER_MONTH * COURTS; // 1 050
    const HC_SLOTS_PER_DAY = HC_HOURS_PER_DAY * COURTS;
    const HP_SLOTS_PER_DAY = HP_HOURS_PER_DAY * COURTS;
    const PRICE_HC = 36;
    const PRICE_HP = 52;
    const USERS_PER_BOOKING = 2.5;

    const monthlyOccupancy = [
        { hcRate: 0.51, hpRate: 0.82 },
        { hcRate: 0.53, hpRate: 0.85 },
        { hcRate: 0.49, hpRate: 0.79 },
        { hcRate: 0.61, hpRate: 0.84 },
        { hcRate: 0.69, hpRate: 0.92 },
        { hcRate: 0.76, hpRate: 0.96 },
    ];

    const monthlyTargets = [
        { hcBookings: 964, hpBookings: 861, totalBookings: 1825, totalRevenue: 79472, users: 5562 },
        { hcBookings: 1002, hpBookings: 893, totalBookings: 1894, totalRevenue: 82471, users: 5635 },
        { hcBookings: 926, hpBookings: 830, totalBookings: 1756, totalRevenue: 76474, users: 5890 },
        { hcBookings: 1153, hpBookings: 882, totalBookings: 2035, totalRevenue: 88000, users: 7088 },
        { hcBookings: 1304, hpBookings: 966, totalBookings: 2270, totalRevenue: 98000, users: 8175 },
        { hcBookings: 1436, hpBookings: 1008, totalBookings: 2444, totalRevenue: 106000, users: 8611 },
    ];

    const dataStart = new Date('2025-08-21');
    const dataEnd = new Date('2026-02-16');
    const transitionDate = new Date('2025-08-21');

    return {
        dataStart,
        dataEnd,
        transitionDate,
        daysPerMonth: DAYS_PER_MONTH,
        hcSlotsPerMonth: HC_SLOTS_PER_MONTH,
        hpSlotsPerMonth: HP_SLOTS_PER_MONTH,
        hcSlotsPerDay: HC_SLOTS_PER_DAY,
        hpSlotsPerDay: HP_SLOTS_PER_DAY,
        priceHc: PRICE_HC,
        priceHp: PRICE_HP,
        usersPerBooking: USERS_PER_BOOKING,
        monthlyOccupancy,
        monthlyTargets,
        monthlyUserBase: [5562, 5635, 5890, 7088, 8175, 8611],
        monthlyBookingsBase: [1825, 1894, 1756, 2035, 2270, 2444],
        monthlyRevenueBase: [79472, 82471, 76474, 88000, 98000, 106000],
        monthlyHcPct: [51, 53, 49, 61, 69, 76],
        monthlyHpPct: [82, 85, 79, 84, 92, 96],
        monthlyEvents: [5, 6, 5, 7, 9, 11],
        monthlySubscriptions: [22, 24, 20, 26, 30, 34],
    };
}

/** Club 4 courts – scénario final: HC 1 080, HP 600 créneaux/mois, Mysportset fin M2 */
function buildClub4CourtsConfig(): DemoDatasetConfig {
    const DAYS_PER_MONTH = 30;
    const HC_SLOTS_PER_MONTH = 1080;
    const HP_SLOTS_PER_MONTH = 600;
    const HC_SLOTS_PER_DAY = HC_SLOTS_PER_MONTH / DAYS_PER_MONTH; // 36
    const HP_SLOTS_PER_DAY = HP_SLOTS_PER_MONTH / DAYS_PER_MONTH; // 20
    const PRICE_HC = 36;
    const PRICE_HP = 52;
    const USERS_PER_BOOKING = 3.7; // approx (utilisateurs posés M1–M2, puis cohérent M3+)

    const monthlyOccupancy = [
        { hcRate: 0.38, hpRate: 0.72 },
        { hcRate: 0.37, hpRate: 0.71 },
        { hcRate: 0.45, hpRate: 0.78 },
        { hcRate: 0.58, hpRate: 0.85 },
        { hcRate: 0.62, hpRate: 0.87 },
        { hcRate: 0.65, hpRate: 0.89 },
    ];

    const monthlyTargets = [
        { hcBookings: 410, hpBookings: 432, totalBookings: 842, totalRevenue: 37224, users: 3117 },
        { hcBookings: 400, hpBookings: 426, totalBookings: 826, totalRevenue: 36552, users: 3216 },
        { hcBookings: 486, hpBookings: 468, totalBookings: 954, totalRevenue: 41832, users: 3778 },
        { hcBookings: 626, hpBookings: 510, totalBookings: 1136, totalRevenue: 49056, users: 4408 },
        { hcBookings: 670, hpBookings: 522, totalBookings: 1192, totalRevenue: 50952, users: 5176 },
        { hcBookings: 702, hpBookings: 534, totalBookings: 1236, totalRevenue: 53388, users: 5308 },
    ];

    const dataStart = new Date('2025-08-21');
    const dataEnd = new Date('2026-02-16');
    const transitionDate = new Date('2025-08-21');

    return {
        dataStart,
        dataEnd,
        transitionDate,
        daysPerMonth: DAYS_PER_MONTH,
        hcSlotsPerMonth: HC_SLOTS_PER_MONTH,
        hpSlotsPerMonth: HP_SLOTS_PER_MONTH,
        hcSlotsPerDay: HC_SLOTS_PER_DAY,
        hpSlotsPerDay: HP_SLOTS_PER_DAY,
        priceHc: PRICE_HC,
        priceHp: PRICE_HP,
        usersPerBooking: USERS_PER_BOOKING,
        monthlyOccupancy,
        monthlyTargets,
        monthlyUserBase: [3117, 3216, 3778, 4408, 5176, 5308],
        monthlyBookingsBase: [842, 826, 954, 1136, 1192, 1236],
        monthlyRevenueBase: [37224, 36552, 41832, 49056, 50952, 53388],
        monthlyHcPct: [38, 37, 45, 58, 62, 65],
        monthlyHpPct: [72, 71, 78, 85, 87, 89],
        monthlyEvents: [3, 3, 4, 6, 6, 7],
        monthlySubscriptions: [12, 14, 16, 20, 22, 24],
    };
}

const DEMO_DATASETS: Record<string, DemoDatasetConfig> = {
    [DEFAULT_DATASET_KEY]: buildDefaultDatasetConfig(),
    'club1-scenario2': buildClub1Scenario2Config(),
    'club4-courts': buildClub4CourtsConfig(),
};

function getDatasetConfig(datasetKey: string): DemoDatasetConfig {
    return DEMO_DATASETS[datasetKey] ?? DEMO_DATASETS[DEFAULT_DATASET_KEY];
}

/** Date the club adopted MySportSet – the inflection point. */
export function getTransitionDate(orgId?: string): Date {
    return getDatasetConfig(getDatasetKey(orgId)).transitionDate;
}
/** Legacy export for default dataset (use getTransitionDate(orgId) when in demo mode). */
export const TRANSITION_DATE = new Date('2025-08-21');

export function getTransitionTimestamp(orgId?: string): number {
    return getTransitionDate(orgId).getTime();
}
/** Visible range starts at end of month 1 so ticks land at month-end boundaries. */
export function getDemoXAxisMin(orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    return cfg.transitionDate.getTime() + 30 * 86_400_000;
}
export function getDemoXAxisMax(orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    return cfg.transitionDate.getTime() + 6 * 30 * 86_400_000;
}
/** Backward compatibility: default org axis range. */
export const DEMO_XAXIS_MIN = getDemoXAxisMin(DEMO_ORGANIZATION_ID);
export const DEMO_XAXIS_MAX = getDemoXAxisMax(DEMO_ORGANIZATION_ID);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysBetween(a: Date, b: Date): number {
    return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function eachDay(from: Date, to: Date): Date[] {
    const days: Date[] = [];
    const cur = new Date(from);
    while (cur <= to) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return days;
}

/** Simple deterministic pseudo-random (mulberry32). */
function seededRandom(seed: number): () => number {
    return () => {
        seed |= 0;
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Distribute a monthly target across `numDays` with deterministic noise.
 * The sum of the returned array is guaranteed to equal `target` exactly.
 */
function distributeWithNoise(
    target: number,
    numDays: number,
    rand: () => number,
): number[] {
    if (numDays === 0) return [];
    const weights = Array.from({ length: numDays }, () => 0.85 + rand() * 0.3);
    const wSum = weights.reduce((a, b) => a + b, 0);

    const daily: number[] = [];
    let accumulated = 0;
    for (let d = 0; d < numDays; d++) {
        if (d === numDays - 1) {
            // last day absorbs the residual so the sum is exact
            daily.push(target - accumulated);
        } else {
            const val = target * (weights[d] / wSum);
            daily.push(val);
            accumulated += val;
        }
    }
    return daily;
}

// ---------------------------------------------------------------------------
// Daily data builders
// ---------------------------------------------------------------------------

interface DailyData {
    date: string;
    hcBookings: number;
    hpBookings: number;
    totalBookings: number;
    totalRevenue: number;
    users: number;
}

/**
 * Build daily data for the full timeline.
 * Pre-transition: all zeros.
 * Post-transition: each 30-day "business month" hits its exact target,
 * distributed across days with small noise.
 */
function buildDailyData(seed: number, cfg: DemoDatasetConfig): DailyData[] {
    const rand = seededRandom(seed);
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    const result: DailyData[] = [];

    // Pre-transition: zeros
    for (let i = 0; i < transitionDay && i < days.length; i++) {
        result.push({
            date: days[i].toISOString().slice(0, 10),
            hcBookings: 0, hpBookings: 0, totalBookings: 0,
            totalRevenue: 0, users: 0,
        });
    }

    // Post-transition: month-by-month blocks
    let dayIdx = transitionDay;
    for (let month = 0; month < cfg.monthlyTargets.length && dayIdx < days.length; month++) {
        const { hcBookings: monthlyHC, hpBookings: monthlyHP } = cfg.monthlyTargets[month];
        const daysInMonth = Math.min(cfg.daysPerMonth, days.length - dayIdx);
        const proRata = daysInMonth / cfg.daysPerMonth;

        const hcDaily = distributeWithNoise(monthlyHC * proRata, daysInMonth, rand);
        const hpDaily = distributeWithNoise(monthlyHP * proRata, daysInMonth, rand);

        for (let d = 0; d < daysInMonth; d++) {
            const hc = hcDaily[d];
            const hp = hpDaily[d];
            const total = hc + hp;
            result.push({
                date: days[dayIdx + d].toISOString().slice(0, 10),
                hcBookings: hc,
                hpBookings: hp,
                totalBookings: total,
                totalRevenue: hc * cfg.priceHc + hp * cfg.priceHp,
                users: total * cfg.usersPerBooking,
            });
        }
        dayIdx += daysInMonth;
    }

    // Any remaining days beyond the 6 months use the last month's daily rates
    while (dayIdx < days.length) {
        const last = cfg.monthlyTargets[cfg.monthlyTargets.length - 1];
        const dailyHC = last.hcBookings / cfg.daysPerMonth;
        const dailyHP = last.hpBookings / cfg.daysPerMonth;
        const total = dailyHC + dailyHP;
        result.push({
            date: days[dayIdx].toISOString().slice(0, 10),
            hcBookings: dailyHC,
            hpBookings: dailyHP,
            totalBookings: total,
            totalRevenue: dailyHC * cfg.priceHc + dailyHP * cfg.priceHp,
            users: total * cfg.usersPerBooking,
        });
        dayIdx++;
    }

    return result;
}

/** Generate daily event data (sparse, probabilistic). */
function buildDailyEvents(seed: number, cfg: DemoDatasetConfig): { date: string; count: number }[] {
    const rand = seededRandom(seed);
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    return days.map((d, i) => {
        const dateStr = d.toISOString().slice(0, 10);
        if (i < transitionDay) return { date: dateStr, count: 0 };

        const daysSinceTransition = i - transitionDay;
        const monthIndex = Math.min(
            Math.floor(daysSinceTransition / cfg.daysPerMonth),
            cfg.monthlyEvents.length - 1,
        );
        const hasEvent = rand() < (cfg.monthlyEvents[monthIndex] / cfg.daysPerMonth) ? 1 : 0;
        return { date: dateStr, count: hasEvent };
    });
}

// ---------------------------------------------------------------------------
// Cached series (lazily built per dataset key)
// ---------------------------------------------------------------------------

const _dailyDataByKey: Record<string, DailyData[]> = {};
const _eventDataByKey: Record<string, { date: string; count: number }[]> = {};

function getDailyData(datasetKey: string): DailyData[] {
    if (!_dailyDataByKey[datasetKey]) _dailyDataByKey[datasetKey] = buildDailyData(100, getDatasetConfig(datasetKey));
    return _dailyDataByKey[datasetKey];
}

function getEventData(datasetKey: string): { date: string; count: number }[] {
    if (!_eventDataByKey[datasetKey]) _eventDataByKey[datasetKey] = buildDailyEvents(606, getDatasetConfig(datasetKey));
    return _eventDataByKey[datasetKey];
}

// ---------------------------------------------------------------------------
// Cumulative helpers
// ---------------------------------------------------------------------------

function toCumulative(
    daily: { date: string }[],
    getValue: (d: any) => number,
): { date: string; value: number }[] {
    let cum = 0;
    return daily.map(d => {
        cum += getValue(d);
        return { date: d.date, value: cum };
    });
}

// Cached cumulative series for stat card lookups (per dataset)
const _userCumSeriesByKey: Record<string, { date: string; value: number }[]> = {};
const _bookingsCumSeriesByKey: Record<string, { date: string; value: number }[]> = {};
const _revenueCumSeriesByKey: Record<string, { date: string; value: number }[]> = {};
const _hoursCumSeriesByKey: Record<string, { date: string; value: number }[]> = {};
const _eventCumSeriesByKey: Record<string, { date: string; value: number }[]> = {};

function getUserCumSeries(datasetKey: string) {
    if (!_userCumSeriesByKey[datasetKey]) _userCumSeriesByKey[datasetKey] = toCumulative(getDailyData(datasetKey), (d: DailyData) => d.users);
    return _userCumSeriesByKey[datasetKey];
}
function getBookingsCumSeries(datasetKey: string) {
    if (!_bookingsCumSeriesByKey[datasetKey]) _bookingsCumSeriesByKey[datasetKey] = toCumulative(getDailyData(datasetKey), (d: DailyData) => d.totalBookings);
    return _bookingsCumSeriesByKey[datasetKey];
}
function getRevenueCumSeries(datasetKey: string) {
    if (!_revenueCumSeriesByKey[datasetKey]) _revenueCumSeriesByKey[datasetKey] = toCumulative(getDailyData(datasetKey), (d: DailyData) => d.totalRevenue);
    return _revenueCumSeriesByKey[datasetKey];
}
function getHoursCumSeries(datasetKey: string) {
    if (!_hoursCumSeriesByKey[datasetKey]) _hoursCumSeriesByKey[datasetKey] = toCumulative(getDailyData(datasetKey), (d: DailyData) => d.totalBookings);
    return _hoursCumSeriesByKey[datasetKey];
}
function getEventCumSeries(datasetKey: string) {
    if (!_eventCumSeriesByKey[datasetKey]) _eventCumSeriesByKey[datasetKey] = toCumulative(getEventData(datasetKey), (d: { count: number }) => d.count);
    return _eventCumSeriesByKey[datasetKey];
}

/** Look up the cumulative value at (or nearest before) a given date. */
function getValueAtDate(series: { date: string; value: number }[], target: Date): number {
    const targetStr = target.toISOString().slice(0, 10);
    if (targetStr <= series[0].date) return 0;
    if (targetStr >= series[series.length - 1].date) return series[series.length - 1].value;
    for (let i = series.length - 1; i >= 0; i--) {
        if (series[i].date <= targetStr) return series[i].value;
    }
    return 0;
}

/** Get the delta (growth) within a date range from a cumulative series. */
function getDelta(series: { date: string; value: number }[], start: Date, end: Date): number {
    return Math.max(0, getValueAtDate(series, end) - getValueAtDate(series, start));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function isDemoOrganization(orgId?: string): boolean {
    return !!orgId && DEMO_ORGANIZATION_IDS.includes(orgId);
}

// -- Cumulative User Signups (total user base over time) ----------------------

export function getDemoCumulativeUserSignups(orgId?: string): { date: string; count: number }[] {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    return days.map((d, i) => {
        const dateStr = d.toISOString().slice(0, 10);
        if (i < transitionDay) return { date: dateStr, count: 0 };

        const daysSinceTransition = i - transitionDay;
        const monthIndex = Math.floor(daysSinceTransition / cfg.daysPerMonth);
        const dayInMonth = daysSinceTransition % cfg.daysPerMonth;

        if (monthIndex >= cfg.monthlyUserBase.length) {
            return { date: dateStr, count: cfg.monthlyUserBase[cfg.monthlyUserBase.length - 1] };
        }

        const prevBase = monthIndex === 0 ? cfg.monthlyUserBase[0] : cfg.monthlyUserBase[monthIndex - 1];
        const currentBase = cfg.monthlyUserBase[monthIndex];
        const progress = (dayInMonth + 1) / cfg.daysPerMonth;
        const interpolated = prevBase + (currentBase - prevBase) * progress;

        return { date: dateStr, count: Math.round(interpolated) };
    });
}

// -- Cumulative Bookings (total reservations over time) -----------------------

export function getDemoCumulativeBookings(orgId?: string): { date: string; count: number }[] {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    return days.map((d, i) => {
        const dateStr = d.toISOString().slice(0, 10);
        if (i < transitionDay) return { date: dateStr, count: 0 };

        const daysSinceTransition = i - transitionDay;
        const monthIndex = Math.floor(daysSinceTransition / cfg.daysPerMonth);
        const dayInMonth = daysSinceTransition % cfg.daysPerMonth;

        if (monthIndex >= cfg.monthlyBookingsBase.length) {
            return { date: dateStr, count: Math.round(cfg.monthlyBookingsBase[cfg.monthlyBookingsBase.length - 1]) };
        }

        const prevBase = monthIndex === 0 ? cfg.monthlyBookingsBase[0] : cfg.monthlyBookingsBase[monthIndex - 1];
        const currentBase = cfg.monthlyBookingsBase[monthIndex];
        const progress = (dayInMonth + 1) / cfg.daysPerMonth;
        const interpolated = prevBase + (currentBase - prevBase) * progress;

        return { date: dateStr, count: Math.round(interpolated) };
    });
}

// -- Cumulative Revenue (total revenue over time) -----------------------------

export function getDemoCumulativeRevenue(orgId?: string): { date: string; amount: number }[] {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    return days.map((d, i) => {
        const dateStr = d.toISOString().slice(0, 10);
        if (i < transitionDay) return { date: dateStr, amount: 0 };

        const daysSinceTransition = i - transitionDay;
        const monthIndex = Math.floor(daysSinceTransition / cfg.daysPerMonth);
        const dayInMonth = daysSinceTransition % cfg.daysPerMonth;

        if (monthIndex >= cfg.monthlyRevenueBase.length) {
            return { date: dateStr, amount: +cfg.monthlyRevenueBase[cfg.monthlyRevenueBase.length - 1].toFixed(2) };
        }

        const prevBase = monthIndex === 0 ? cfg.monthlyRevenueBase[0] : cfg.monthlyRevenueBase[monthIndex - 1];
        const currentBase = cfg.monthlyRevenueBase[monthIndex];
        const progress = (dayInMonth + 1) / cfg.daysPerMonth;
        const interpolated = prevBase + (currentBase - prevBase) * progress;

        return { date: dateStr, amount: +interpolated.toFixed(2) };
    });
}

// -- Occupancy % (replaces cumulative hours in demo mode) ---------------------

export function getDemoCumulativeHoursBooked(orgId?: string): {
    total: { date: string; hours: number }[];
    byTimeWindow: { groupId: string; groupName: string; data: { date: string; hours: number }[] }[];
} {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const days = eachDay(cfg.dataStart, cfg.dataEnd);
    const transitionDay = daysBetween(cfg.dataStart, cfg.transitionDate);

    const hcSeries: { date: string; hours: number }[] = [];
    const hpSeries: { date: string; hours: number }[] = [];

    for (let i = 0; i < days.length; i++) {
        const dateStr = days[i].toISOString().slice(0, 10);
        if (i < transitionDay) {
            hcSeries.push({ date: dateStr, hours: 0 });
            hpSeries.push({ date: dateStr, hours: 0 });
            continue;
        }

        const daysSinceTransition = i - transitionDay;
        const monthIndex = Math.floor(daysSinceTransition / cfg.daysPerMonth);
        const dayInMonth = daysSinceTransition % cfg.daysPerMonth;

        if (monthIndex >= cfg.monthlyHcPct.length) {
            hcSeries.push({ date: dateStr, hours: cfg.monthlyHcPct[cfg.monthlyHcPct.length - 1] });
            hpSeries.push({ date: dateStr, hours: cfg.monthlyHpPct[cfg.monthlyHpPct.length - 1] });
            continue;
        }

        const progress = (dayInMonth + 1) / cfg.daysPerMonth;

        const prevHC = monthIndex === 0 ? cfg.monthlyHcPct[0] : cfg.monthlyHcPct[monthIndex - 1];
        const curHC = cfg.monthlyHcPct[monthIndex];
        const hcVal = +(prevHC + (curHC - prevHC) * progress).toFixed(2);

        const prevHP = monthIndex === 0 ? cfg.monthlyHpPct[0] : cfg.monthlyHpPct[monthIndex - 1];
        const curHP = cfg.monthlyHpPct[monthIndex];
        const hpVal = +(prevHP + (curHP - prevHP) * progress).toFixed(2);

        hcSeries.push({ date: dateStr, hours: hcVal });
        hpSeries.push({ date: dateStr, hours: hpVal });
    }

    return {
        total: [],
        byTimeWindow: [
            { groupId: 'peak', groupName: 'Heures pleines (HP)', data: hpSeries },
            { groupId: 'offpeak', groupName: 'Heures creuses (HC)', data: hcSeries },
        ],
    };
}

// -- Stat card helpers (date-range aware) -------------------------------------

/**
 * Given a date range, return the 0-based month indices that fall within it.
 * Month 0 = days 0-29 from TRANSITION_DATE, Month 1 = days 30-59, etc.
 */
function monthsInRange(startDate: Date, endDate: Date, cfg: DemoDatasetConfig): number[] {
    const startDays = Math.max(0, daysBetween(cfg.transitionDate, startDate));
    const endDays = Math.max(0, daysBetween(cfg.transitionDate, endDate));
    const maxMonth = cfg.monthlyUserBase.length - 1;
    const startMonth = Math.min(Math.floor(startDays / cfg.daysPerMonth), maxMonth);
    const endMonth = Math.min(Math.floor(Math.max(0, endDays - 1) / cfg.daysPerMonth), maxMonth);

    const months: number[] = [];
    for (let m = startMonth; m <= endMonth; m++) months.push(m);
    return months;
}

function sumMonths(arr: number[], months: number[]): number {
    return months.reduce((sum, m) => sum + arr[m], 0);
}

export function getDemoUserCount(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const months = (!startDate || !endDate) ? cfg.monthlyUserBase.map((_, i) => i) : monthsInRange(startDate, endDate, cfg);
    if (months.length === 0) return 0;
    if (months.length >= cfg.monthlyUserBase.length) return cfg.monthlyUserBase[cfg.monthlyUserBase.length - 1];
    return Math.round(sumMonths(cfg.monthlyUserBase, months) / months.length);
}

export function getDemoGameCount(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    if (!startDate || !endDate) {
        return Math.round(cfg.monthlyBookingsBase.reduce((a, b) => a + b, 0));
    }
    return Math.round(sumMonths(cfg.monthlyBookingsBase, monthsInRange(startDate, endDate, cfg)));
}

export function getDemoGameCountByType(orgId?: string) {
    const total = getDemoGameCount(undefined, undefined, orgId);
    // Realistic distribution: ~65% ranked-public, ~15% ranked-private, ~13% unranked-public, ~7% unranked-private
    return {
        items: [
            { gameType: 'public' as const, rankedMode: 'ranked' as const, count: Math.round(total * 0.65) },
            { gameType: 'private' as const, rankedMode: 'ranked' as const, count: Math.round(total * 0.15) },
            { gameType: 'public' as const, rankedMode: 'unranked' as const, count: Math.round(total * 0.13) },
            { gameType: 'private' as const, rankedMode: 'unranked' as const, count: Math.round(total * 0.07) },
        ],
    };
}

export function getDemoEventCount(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    if (!startDate || !endDate) {
        return cfg.monthlyEvents.reduce((a, b) => a + b, 0);
    }
    return Math.round(sumMonths(cfg.monthlyEvents, monthsInRange(startDate, endDate, cfg)));
}

export function getDemoGameHours(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    if (!startDate || !endDate) {
        return +cfg.monthlyBookingsBase.reduce((a, b) => a + b, 0).toFixed(1);
    }
    return +sumMonths(cfg.monthlyBookingsBase, monthsInRange(startDate, endDate, cfg)).toFixed(1);
}

export function getDemoRevenue(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    if (!startDate || !endDate) {
        return +cfg.monthlyRevenueBase.reduce((a, b) => a + b, 0).toFixed(2);
    }
    return +sumMonths(cfg.monthlyRevenueBase, monthsInRange(startDate, endDate, cfg)).toFixed(2);
}

export function getDemoSubscriptionCount(startDate?: Date, endDate?: Date, orgId?: string): number {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    if (!startDate || !endDate) {
        return cfg.monthlySubscriptions.reduce((a, b) => a + b, 0);
    }
    return Math.round(sumMonths(cfg.monthlySubscriptions, monthsInRange(startDate, endDate, cfg)));
}

/**
 * Compute demo growth between a current and previous period.
 * Returns undefined when no previous period is provided.
 */
export function getDemoGrowth(
    currentValue: number,
    previousStartDate: Date | null | undefined,
    previousEndDate: Date | null | undefined,
    getter: (start?: Date, end?: Date, orgId?: string) => number,
    orgId?: string,
): number | undefined {
    if (!previousStartDate || !previousEndDate) return undefined;
    const prev = getter(previousStartDate, previousEndDate, orgId);
    if (prev === 0) return currentValue > 0 ? 100 : 0;
    return +((currentValue - prev) / prev * 100).toFixed(1);
}

/**
 * Usage ratio computed from actual bookings vs available slots in the period.
 */
export function getDemoCourtsUsageRatio(startDate: Date, endDate: Date, orgId?: string): number {
    const key = getDatasetKey(orgId);
    const cfg = getDatasetConfig(key);
    const data = getDailyData(key);
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const transitionStr = cfg.transitionDate.toISOString().slice(0, 10);

    let totalBookings = 0;
    let activeDays = 0;

    for (const d of data) {
        if (d.date >= startStr && d.date <= endStr && d.date >= transitionStr) {
            totalBookings += d.totalBookings;
            activeDays++;
        }
    }

    if (activeDays === 0) return 0;
    const totalAvailableSlots = activeDays * (cfg.hcSlotsPerDay + cfg.hpSlotsPerDay);
    return totalBookings / totalAvailableSlots;
}

// -- Heatmap ------------------------------------------------------------------

/** 7×24 matrix (days × hours). Reflects HC/HP distribution. */
export function getDemoHeatmap(orgId?: string): { value: number[][] } {
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const rand = seededRandom(505);
    const value: number[][] = [];

    const latestMonth = Math.min(
        Math.floor(daysBetween(cfg.transitionDate, cfg.dataEnd) / cfg.daysPerMonth),
        cfg.monthlyOccupancy.length - 1,
    );
    const latestOcc = cfg.monthlyOccupancy[latestMonth];

    for (let day = 0; day < 7; day++) {
        const row: number[] = [];
        for (let hour = 0; hour < 24; hour++) {
            let base = 0;

            if ((hour >= 7 && hour < 12) || (hour >= 14 && hour < 17)) {
                base = 20 + latestOcc.hcRate * 50 + rand() * 15;
            }
            else if ((hour >= 12 && hour < 14) || (hour >= 17 && hour < 22)) {
                base = 35 + latestOcc.hpRate * 45 + rand() * 20;
            }
            else if (hour >= 6 && hour < 7) {
                base = 5 + rand() * 8;
            }
            else {
                base = rand() * 3;
            }

            if ((day === 0 || day === 6) && hour >= 9 && hour < 20) base *= 1.15;
            if (day === 1) base *= 0.85;

            row.push(Math.round(base));
        }
        value.push(row);
    }
    return { value };
}

// -- User Levels Distribution -------------------------------------------------

export function getDemoUserLevelsDistribution(): { level: string; count: number }[] {
    return [
        { level: '1', count: 85 },
        { level: '2', count: 156 },
        { level: '3', count: 289 },
        { level: '4', count: 412 },
        { level: '5', count: 387 },
        { level: '6', count: 298 },
        { level: '7', count: 234 },
        { level: '8', count: 178 },
        { level: '9', count: 67 },
        { level: '10', count: 12 },
    ];
}

// -- X-axis label helper (shows "Mois N" instead of dates) --------------------

/**
 * Convert a timestamp to "Mois N" label for demo charts.
 * Returns '' for pre-transition dates.
 */
export function formatDemoMonth(timestampOrVal: number | string, orgId?: string): string {
    const ts = typeof timestampOrVal === 'string' ? Number(timestampOrVal) : timestampOrVal;
    if (isNaN(ts)) return '';
    const transitionTs = getTransitionTimestamp(orgId);
    const cfg = getDatasetConfig(getDatasetKey(orgId));
    const daysDiff = Math.round((ts - transitionTs) / 86_400_000);
    if (daysDiff <= 0) return 'Mois 1';
    const monthLabel = Math.min(Math.ceil(daysDiff / cfg.daysPerMonth), 6);
    return `Mois ${monthLabel}`;
}

// -- ApexCharts annotations helper --------------------------------------------

export function getDemoAnnotations(orgId?: string): ApexAnnotations {
    const transitionTs = getTransitionTimestamp(orgId);
    return {
        xaxis: [
            {
                x: transitionTs + 90 * 86_400_000,
                borderColor: '#775DD0',
                strokeDashArray: 0,
                borderWidth: 2,
                label: {
                    text: 'Début MySportSet',
                    orientation: 'vertical',
                    borderColor: '#775DD0',
                    style: {
                        color: '#fff',
                        background: '#775DD0',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: { left: 8, right: 8, top: 4, bottom: 4 },
                    },
                },
            },
        ],
    };
}
